import { NavLink, useLocation } from 'react-router';
import { cn } from '../../lib/cn';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useMe } from '../../hooks/useMe';
import { NAV_SECTIONS, navItemState, wsUrl, type NavSection } from '../nav';
import { ShieldCheck } from 'lucide-react';

function sectionActive(section: NavSection, wsId: string, pathname: string): boolean {
  const base = wsUrl(wsId);
  const rest = pathname.startsWith(base) ? pathname.slice(base.length).replace(/^\//, '') : '';
  if (section.key === 'home') return rest === '' || rest.startsWith('this-week');
  return rest === section.path || rest.startsWith(`${section.path}/`);
}

/** Desktop (>= 1024px) sidebar: sections with their sub-screens (hidden when switched off or not permitted). */
export function Sidebar({ className }: { className?: string }) {
  const { wsId, workspace } = useWorkspace();
  const { pathname } = useLocation();
  const me = useMe();
  return (
    <nav aria-label="Main" className={cn('flex flex-col gap-1 overflow-y-auto p-3', className)}>
      <ul className="flex flex-col gap-1">
        {NAV_SECTIONS.map((section) => {
          const active = sectionActive(section, wsId, pathname);
          const items = section.items.filter((i) => navItemState(i, workspace) === 'on');
          const Icon = section.icon;
          return (
            <li key={section.key}>
              <NavLink
                to={wsUrl(wsId, section.path)}
                end
                className={({ isActive }) =>
                  cn(
                    'flex min-h-11 items-center gap-3 rounded-lg px-3 font-bold no-underline',
                    isActive ? 'bg-primary text-on-primary' : active ? 'text-primary' : 'text-text hover:bg-surface-muted',
                  )
                }
              >
                <Icon aria-hidden="true" className="h-5 w-5" />
                {section.label}
              </NavLink>
              {items.length > 0 && (
                <ul className="mt-1 mb-2 ml-5 flex flex-col border-l-2 border-border pl-2">
                  {items.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={wsUrl(wsId, item.path)}
                        className={({ isActive }) =>
                          cn(
                            'flex min-h-10 items-center rounded-md px-3 no-underline',
                            isActive ? 'bg-primary-soft font-bold text-primary' : 'text-text hover:bg-surface-muted',
                          )
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
        {me.data?.user.isPlatformAdmin && (
          <li>
            <NavLink to="/admin" className="flex min-h-11 items-center gap-3 rounded-lg px-3 font-bold text-text no-underline hover:bg-surface-muted">
              <ShieldCheck aria-hidden="true" className="h-5 w-5" />
              Admin
            </NavLink>
          </li>
        )}
      </ul>
    </nav>
  );
}

/** Mobile bottom tab bar: Home · Money · Health · Records · More (spec §11). */
export function BottomNav({ className }: { className?: string }) {
  const { wsId } = useWorkspace();
  const { pathname } = useLocation();
  return (
    <nav aria-label="Main" className={cn('pb-safe border-t border-border bg-surface', className)}>
      <ul className="grid grid-cols-5">
        {NAV_SECTIONS.map((section) => {
          const active = sectionActive(section, wsId, pathname);
          const Icon = section.icon;
          return (
            <li key={section.key} className="min-w-0">
              <NavLink
                to={wsUrl(wsId, section.path)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  // Tab labels/icons are capped (like native tab bars) so five tabs still fit at 200% text size (R-5.7).
                  'flex min-h-14 min-w-0 flex-col items-center justify-center gap-0.5 px-1 text-center text-[clamp(12px,0.75rem,16px)] font-bold no-underline sm:text-[clamp(14px,0.875rem,18px)]',
                  active ? 'text-primary' : 'text-muted hover:text-text',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn('inline-flex h-[min(1.75rem,36px)] w-[min(3rem,64px)] items-center justify-center rounded-full', active && 'bg-primary-soft')}
                >
                  <Icon className="h-5 w-5" />
                </span>
                {section.label}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
